import React from "react";
import {History} from "react-router";
import Chip from "material-ui/Chip";
import {blueGrey50, red300} from "material-ui/styles/colors";
import PageAncestor from "core/common/page/pageAncestor";
import Toolmenu from "core/components/toolmenu/toolmenu";
import mdEntityService from "core/metamodel/mdEntityService";
import securityService from "core/security/securityService";
import {BlockComp, MdDivider} from "core/components/blockComp/blockComp";
import {preSave, IDomainObject, lovValue} from "core/form/formUtils";
import {ErrorComp} from "core/components/errorComp/errorComp";
import UserForm from "core/security/userForm";
import gridService from "core/grid/gridService";
import GridComp from "core/grid/component/gridComp";
import ConfirmationDialog from "core/components/confirmationDialog/confirmationDialog";
import Grid, {GridRow} from "core/grid/domain/grid";
import StyledSelect from 'core/components/fields/styledSelect';
import ActiveItem from 'core/components/blockComp/activeItem';
import {lovLabel} from "core/form/formUtils";
import {fieldStyles} from "core/form/createForm";
import {MODULE, PERMISSION, SECURABLE_TYPE, SECURABLE, PageProps} from "core/metamodel/metaConstant";
import {WithDeleteDialogZzFlatButton, ZzFlatButton} from "core/components/buttons/buttons";
import {SecurableDefinitionModuleMap, User} from "core/security/securityDomain";
import {MovingBlocks} from "core/grid/gridUtils";
import {action} from "mobx";
import uiService from "core/common/service/uiService";
import {PartyRole, Party, PARTYROLE, PARTYCATEGORY} from "party/partyDomain";
import {UILOCATION} from "core/grid/component/gridComp";
import {observer} from "mobx-react";
import partyService from 'party/partyService';
import SvgMoreVert from "material-ui/svg-icons/navigation/more-vert";
import {ZzIconButtonMenu, ZzMenuItem} from "core/components/buttons/icons";
import UserTokenList from "core/security/component/userTokenList";
import {loc} from "../common/config/locales";
import {addTenantToQuery} from "../common/utils/utils";
import {Link} from 'react-router';
import {SecurablesResolved} from "./component/securableResolved";

const tenantGridLocation = 'tenantListDefault';


@observer
export default class UserDetail extends PageAncestor<PageProps, undefined> {

  static module = MODULE.core;
  static title = 'User';
  static icon = 'user';
  static protectedPage = true;


  static fetchData(routerParams, query) {
    console.log("UserDetail#fetchData()");

    let metadataPromise = mdEntityService.fetchEntityMetadata(['User', 'UserToken'], ['Tenant', 'Party', 'PartyContact', 'PartyRole', 'Address', 'PartyNote',
      {entity: 'PartyRoleType', lov: true}, {entity: 'SecurityRole', lov: true}, {entity: 'ProfitCenter', lov: true}, {entity: 'MODULE', lov: true}, {entity: 'SECURABLETYPE', lov: true}, {entity: 'SECURABLE', lov: true}]);

    let userPromise: Promise<User>;
    if (routerParams.id === 'new') {
      userPromise = Promise.resolve(new User(Object.assign({
        gridConfigs: [],
        tenants: [],
        securityRoles: [],
        restrictions: [],
        defaultGridConfig: {},
        permissionMap: {},
        profitCenters: [],
        tokens: [],
        enabled: true,
        expirationMinutes: 480,
        rememberMe: false,
        passwordPolicy: 2,
        $new: true
      }, query)));
    } else if (routerParams.id === 'copy') {
      userPromise = securityService.readUser(query.copyId)
        .then(userObject => {
          userObject._copiedFromUsername = userObject.username;
          userObject.cleanOnCopy();
          userObject.gridConfigs = [];
          userObject.defaultGridConfig = {};
          userObject.permissionMap = {};
          userObject.tokens = [];
          return userObject;
        });
    } else {
      userPromise = securityService.readUser(routerParams.id);
    }

    let gridPromise = gridService.fetchGrids(tenantGridLocation);
    let securablesPromise = securityService.getSecurables();

    return Promise.all([metadataPromise, userPromise, gridPromise, securablesPromise])
      .then(([entityMap, userObject, gridLocationMap, securables]: [any, User, any, SecurableDefinitionModuleMap]) => {
        userObject.$movingBlocks = new MovingBlocks(null, false, null, new Grid(gridLocationMap.get(tenantGridLocation)));
        securityService.userObject = userObject.initAfterRead(routerParams.id === 'new');
        securityService.securables = securables;
        return securityService.userObject;
      })
  }


  /**
   * tohle je tady kvuli prechodu na jiny detail party (z relationship vazeb), kdy react-router neudela reload
   * @param prevProps
   */
  componentDidUpdate(prevProps) {
    console.log("componentDidUpdate prev.id = %s, id = %s", prevProps.params.id, this.props.params.id);
    if(prevProps.params.id !== this.props.params.id) {
      securityService.userObject.$movingBlocks.searchExposedGrids(securityService.userObject);
    }
  }


  componentDidMount() {
    console.log("ComponentDidMount");
    securityService.userObject.$movingBlocks.searchExposedGrids(securityService.userObject);
  }


  componentWillUnmount() {
    securityService.userObject = null;
  }

  customValidate = (userObject: User) => {
    let errors = [];
    if (userObject.tenants.length === 0) {
      errors.push({message: "At least one tenant must be selected"})
    }
    if (!userObject.party) {
      errors.push({message: "Party must be selected"});
    }
    return errors;
  };

  findRootObject() {
    return securityService.userObject;
  }


  @action onSave = (evt) => {
    console.log('onSave');
    uiService.showToolbarActionFlag();

    let result = preSave(securityService.userObject, this.customValidate);
    if (!result) {
      return;
    }

    let promise = (securityService.userObject.$new) ? securityService.userCreate(securityService.userObject) : securityService.userUpdate(securityService.userObject);
    promise.then(action("userSave-action", (userJson: IDomainObject) => {
      let wasNew = securityService.userObject.cleanAfterSave(userJson);
      if(wasNew) {
        this.context.router.replaceWith('userDetail', {id: securityService.userObject.username});
      }
      uiService.hideToolbarActionFlag();
    })).catch(error => {
      console.log("Error %O", error);
      securityService.userObject.$errors = error.getMessages();
    });

  };


  @action onDelete = (evt) => {
    console.log('onDelete');
    securityService.deleteUser(securityService.userObject.username)
      .then(response => {
        this.goBackOrHome();
      }, error => {
        securityService.userObject.$errors = error.getMessages();
      });
  };


  @action onSetPassword = (evt) => {
    console.log('onSetPassword');
    this.context.router.transitionTo('setPassword', {mode: 'ADMIN', id: securityService.userObject.username});
  };

  @action onImpersonate = (evt) => {
    console.log('onImpersonate');
    return securityService.impersonate(securityService.userObject.username)
      .then(userContext => {
        this.context.router.transitionTo('home');
      });
  };


  onAdd = (evt) => {
    console.log('onAdd');
    this.context.router.transitionTo("userDetail", {id: 'new'});
  };

  onCopy = (evt) => {
    console.log('onCopy');
    this.context.router.transitionTo("userDetail", {id: 'copy'}, {copyId: securityService.userObject.username});
  };


  @action onPartyChange = (partyObject) => {
    console.log('onPartyChange %O', partyObject);
    securityService.userObject.party = partyObject;
    securityService.userObject.$dirty = true;
  };


  getSelectTenant = (tenantId: number) => {
    //console.log('getSelectTenant %s .. %s', tenantId, index);
    return securityService.userObject.tenants.includes(tenantId);
  };

  @action setSelectTenant = (tenantId: number, item: GridRow, checked: boolean) => {
    console.log('setSelectTenant %s .. %s...%s', tenantId, item, checked);
    let ti = securityService.userObject.tenants.indexOf(tenantId);
    if (checked) {
      if (ti === -1) securityService.userObject.tenants.push(tenantId);
    } else {
      if (ti >= 0) securityService.userObject.tenants.splice(ti, 1);
    }
    securityService.userObject.$dirty = true;
  };


  truish = () => true;

  @action handleSecurityRole = (valueArray) => {
    console.log('handleSecurityRole %O', valueArray);
    securityService.userObject.securityRoles = valueArray;
    securityService.userObject.$dirty = true;
  };

  @action handleProfitCenter = (valueArray) => {
    console.log('handleProfitCenter %O', valueArray);
    securityService.userObject.profitCenters = valueArray;
    securityService.userObject.$dirty = true;
  };



  newParty = () => new Party({
    partyCategory: PARTYCATEGORY.PE,
    roles: [partyService.newRole(PARTYROLE.USER_ROLE)],
    active: true
  }).initAfterRead(true);


  render() {


    let {
      query
    } = this.props;

    let propsForCreateForm = {
      dataObject: securityService.userObject,
      rootObject: securityService.userObject,
      entity: mdEntityService.getEntity('User'),
      entities: mdEntityService.entityMap
    };

    let movingBlocks = securityService.userObject.$movingBlocks;

    let partyEntity = mdEntityService.getEntity('Party');

    console.debug('%c userDetail render', 'background-color: yellow');


    return (
      <main className="main-content container">
        {this._createToolMenu(securityService.userObject, query.gridLocation)}

        <ErrorComp messages={securityService.userObject.$errors}/>
        <ConfirmationDialog ref={this.dirtyDialogRefHandler}/>
        <ConfirmationDialog ref={this.deleteDialogRefHandler} okButtonText={loc.i18n`Yes`} cancelButtonText={loc.i18n`No`}/>

        <BlockComp noPadding style={{marginTop: 20}} header="User">
          <form >
            <UserForm {...propsForCreateForm} />
          </form>
        </BlockComp>
        <MdDivider inset marginTop={20} marginBottom={20}/>
        <div className="row">
          <div className="col-xs-12 col-sm-6" style={{display: 'flex', flexDirection: 'column'}}>
            <BlockComp header="Security Roles" className="block-comp-right-border">
              <ActiveItem openContent={
                <div className="row">
                  <div className="col-xs-12">
                    <StyledSelect {...fieldStyles.small.styledSelect}
                                  onChange={this.handleSecurityRole} value={securityService.userObject.securityRoles.slice()}
                                  options={mdEntityService.getEntity('SecurityRole').lovItems}
                                  hintText="Choose values" multi fullWidth searchable={true} clearable={false}/>
                  </div>
                </div>
              } closedContent={
                <div
                  style={{fontSize: 14, lineHeight: '14px', minHeight: 20, display: 'flex', flexWrap: 'wrap', margin: -4}}>
                  {securityService.userObject.securityRoles.map( (role, index) => (
                    <Link to="securityRoleDetail" params={{id: lovValue(role)}} query={addTenantToQuery()}>
                      <Chip key={index} style={{margin: 3, backgroundColor: blueGrey50}}>{lovLabel(role)}</Chip>
                    </Link>
                  ))}
                </div>
              } validate={this.truish} dataObject={securityService.userObject} entities={mdEntityService.entityMap} formName="userSecurityRolesForm"
                          rootObject={securityService.userObject} />
            </BlockComp>

            <BlockComp header="Profit Centers" className="block-comp-right-border" style={{flexGrow: 1}}>
              <ActiveItem openContent={
                <div className="row">
                  <div className="col-xs-12">
                    <StyledSelect {...fieldStyles.small.styledSelect}
                                  onChange={this.handleProfitCenter} value={securityService.userObject.profitCenters.slice()}
                                  options={mdEntityService.getEntity('ProfitCenter').lovItems}
                                  hintText="Choose values" multi fullWidth searchable={false} clearable={false}/>
                  </div>
                </div>
              } closedContent={
                <div
                  style={{fontSize: 14, lineHeight: '14px', minHeight: 20, display: 'flex', flexWrap: 'wrap', margin: -4}}>
                  {securityService.userObject.profitCenters.map( (pc, index) => <Chip key={index}
                                                                                        style={{margin: 3, backgroundColor: blueGrey50}}>{lovLabel(pc)}</Chip>)}
                </div>
              } validate={this.truish} dataObject={securityService.userObject} entities={mdEntityService.entityMap} formName="userProfitCentersForm"
                          rootObject={securityService.userObject} />
            </BlockComp>

          </div>
          <div className="col-xs-12 col-sm-6">
            <BlockComp header="Available Tenants">
              <GridComp grid={movingBlocks.getBlock(tenantGridLocation) as Grid} multiSelect={false} hideToolbar={true}
                        gridClassName="detail-grid" bodyStyle={{paddingBottom: 7}}
                        uiLocation={UILOCATION.main} functionMap={{
                getSelectTenant: this.getSelectTenant.bind(this),
                setSelectTenant: this.setSelectTenant.bind(this)
              }} entities={mdEntityService.entityMap}/>
            </BlockComp>
          </div>
        </div>

        <MdDivider inset marginTop={20} marginBottom={20}/>
        <BlockComp noPadding header={<span>Effective Rights</span>} style={{display: 'flex', flexDirection: 'column'}}>
          <SecurablesResolved entities={mdEntityService.entityMap} permissionMap={securityService.userObject.permissionMap} securables={securityService.securables}/>
        </BlockComp>

        <MdDivider inset marginTop={20} marginBottom={20}/>

        <BlockComp noPadding header={<span>Effective Restrictions</span>}>
          <div
            style={{marginLeft: 20, marginTop: 15, display: 'flex', flexWrap: 'wrap'}}>
            {securityService.userObject.restrictions.map( (rst, index) => <Chip key={index}
                                                                                       style={{margin: 3, backgroundColor: red300}}>{lovLabel(rst)}</Chip>)}
          </div>
        </BlockComp>

        <MdDivider inset marginTop={20} marginBottom={20}/>
        <BlockComp noPadding  style={{display: 'flex', flexDirection: 'column'}}>
          <UserTokenList userObject={securityService.userObject} entities={mdEntityService.entityMap}/>
        </BlockComp>


      </main>
    );

  }

  _createToolMenu(userObject: User, gridLocation) {
    return (
      <Toolmenu chainGridLocation={gridLocation} backAction={this.onBack} backDisabled={History.length <= 1} historize entityName="User" entityId={userObject.username}>
        <ZzFlatButton onClick={this.onSave} disabled={!userObject.$dirty} label="Save" permission={userObject.$new ? PERMISSION.New : PERMISSION.Update}/>
        <ZzFlatButton onClick={this.onAdd} disabled={userObject.$new} label="New" permission={PERMISSION.New}/>
        <ZzFlatButton onClick={this.onCopy} disabled={userObject.$new} label="Copy" permission={PERMISSION.New}/>
        <WithDeleteDialogZzFlatButton entityName="User" pageAncestor={this} onClick={this.onDelete} disabled={userObject.$new} label="Delete" permission={PERMISSION.Delete}/>

        <ZzIconButtonMenu buttonIcon={<SvgMoreVert/>} securableType={SECURABLE_TYPE.DETAILACTION}>
          <ZzMenuItem primaryText="Set password" key={1} disabled={userObject.$new} onClick={this.onSetPassword} securable={SECURABLE.setPassword}/>
          <ZzMenuItem primaryText="Impersonate" key={2} disabled={userObject.$new} onClick={this.onImpersonate} securable={SECURABLE.impersonate}/>
        </ZzIconButtonMenu>


      </Toolmenu>
    );
  }

}



// WEBPACK FOOTER //
// ./src/core/security/userDetail.tsx